<template>
  <div>
    <!-- Loading State -->
    <v-container
      v-if="loading"
      style="height: 200px;"
    >
      <v-row
        class="fill-height"
        align-content="center"
        justify="center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-row>
    </v-container>

    <!-- User Not Found -->
    <v-container
      v-else-if="!userFound"
      style="height: 400px;"
    >
      <v-row
        class="fill-height"
        align-content="center"
        justify="center"
      >
        <v-col
          class="text-subtitle-1 text-center"
          cols="12"
        >
          <v-icon
            size="64"
            class="mb-4"
          >
            {{ mdiAccountOff }}
          </v-icon>
          <div>{{ $t("This user profile is not available.") }}</div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Profile Header -->
    <template v-else>
      <header class="profile-header">
        <div class="profile-parchment">
          <div class="profile-avatar-wrapper">
            <img
              class="profile-avatar"
              :src="avatarFailed ? defaultAvatar : avatarUrl"
              :alt="user.name"
              width="220"
              height="220"
              @error="avatarFailed = true"
            >
          </div>
          <div class="profile-info">
            <h1 class="profile-name">
              {{ user.name }}
            </h1>
            <p
              v-if="user.profile_phrase"
              class="profile-phrase"
            >
              {{ user.profile_phrase }}
            </p>
            <div
              class="profile-stars"
              role="img"
              :aria-label="$t(&quot;Profile stars coming soon&quot;)"
              :title="$t(&quot;Stars coming soon&quot;)"
            >
              <v-icon
                v-for="star in 5"
                :key="star"
                aria-hidden="true"
              >
                {{ mdiStarOutline }}
              </v-icon>
            </div>
          </div>
        </div>
      </header>
      <v-container class="profile-meta">
        <div class="profile-connections">
          <p
            v-if="connectionsCount > 0"
            class="profile-friends-count"
          >
            {{ connectionLabel }}
          </p>
          <div
            v-if="connections.length"
            class="profile-friends-avatars"
          >
            <v-avatar
              v-for="(connection, idx) in connections.slice(0, 8)"
              :key="`conn-${idx}`"
              size="32"
              class="friend-avatar"
            >
              <v-img
                :src="getAvatarUrl(connection.avatar) || defaultAvatar"
                :alt="connection.name"
              ></v-img>
            </v-avatar>
          </div>
        </div>
        <div class="profile-actions">
          <!-- Connection Button (only show when logged in and not viewing own profile) -->
          <template v-if="isLoggedIn && !isSelf">
            <!-- Not connected -->
            <v-btn
              v-if="connectionStatus === 'none'"
              color="primary"
              class="action-btn"
              :loading="connectionLoading"
              @click="sendRequest()"
            >
              <v-icon left>
                {{ mdiAccountPlus }}
              </v-icon>{{ $t("\n              Connect\n            ") }}
            </v-btn>

            <!-- Pending outgoing -->
            <v-btn
              v-else-if="connectionStatus === 'pending' && connectionDirection === 'outgoing'"
              outlined
              class="action-btn"
              :loading="connectionLoading"
              @click="cancelRequest()"
            >
              <v-icon left>
                {{ mdiAccountClock }}
              </v-icon>{{ $t("\n              Request Sent\n            ") }}
            </v-btn>

            <!-- Pending incoming -->
            <div
              v-else-if="connectionStatus === 'pending' && connectionDirection === 'incoming'"
              class="d-flex"
            >
              <v-btn
                color="primary"
                class="action-btn me-2"
                :loading="connectionLoading"
                @click="respondRequest('accept')"
              >
                <v-icon left>
                  {{ mdiCheck }}
                </v-icon>{{ $t("\n                Accept\n              ") }}
              </v-btn>
              <v-btn
                outlined
                class="action-btn"
                :loading="connectionLoading"
                @click="respondRequest('reject')"
              >
                <v-icon left>
                  {{ mdiClose }}
                </v-icon>{{ $t("\n                Decline\n              ") }}
              </v-btn>
            </div>

            <!-- Connected -->
            <v-menu
              v-else-if="connectionStatus === 'accepted'"
              bottom
              left
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="success"
                  class="action-btn"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>
                    {{ mdiAccountCheck }}
                  </v-icon>{{ $t("\n                  Connected\n                ") }}
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="removeConnection()">
                  <v-list-item-icon>
                    <v-icon
                      size="20"
                      color="error"
                    >
                      {{ mdiAccountRemove }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="error--text">
                      {{ $t("\n                      Remove Connection\n                    ") }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <!-- Message button (always show) -->
          <v-btn
            outlined
            class="action-btn ms-2"
            @click="messageSnackbar = true"
          >
            <v-icon left>
              {{ mdiMessage }}
            </v-icon>{{ $t("\n            Message\n          ") }}
          </v-btn>
        </div>
      </v-container>

      <!-- Navigation Tabs -->
      <v-container class="profile-tabs-container">
        <v-card class="mb-4">
          <v-tabs
            :value="activeTab"
            show-arrows
          >
            <v-tab @click="navigateTo('public_all')">
              {{ $t("\n              All\n            ") }}
            </v-tab>
            <v-tab @click="navigateTo('public_pictures')">
              {{ $t("\n              Pictures\n            ") }}
            </v-tab>
            <v-tab @click="navigateTo('public_biography')">
              {{ biographyLabel }}
            </v-tab>
          </v-tabs>
        </v-card>
      </v-container>

      <v-snackbar
        v-model="messageSnackbar"
        :timeout="3000"
        top
      >
        {{ $t("\n        Messaging feature coming soon!\n        ") }}<template v-slot:action="{ attrs }">
          <v-btn
            text
            v-bind="attrs"
            @click="messageSnackbar = false"
          >
            {{ $t("\n            Close\n          ") }}
          </v-btn>
        </template>
      </v-snackbar>
    </template>
  </div>
</template>

<script>

import axios from 'axios'
import {
  mdiMessage,
  mdiStarOutline,
  mdiAccountOff,
  mdiAccountPlus,
  mdiAccountClock,
  mdiAccountCheck,
  mdiAccountRemove,
  mdiCheck,
  mdiClose,
} from '@mdi/js'
import { t } from '@/i18n'

export default {
  name: 'PublicProfileHeader',
  props: {
    contentType: {
      type: Number,
      default: 0,
    },
    activeTab: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    mdiMessage,
    mdiStarOutline,
    mdiAccountOff,
    mdiAccountPlus,
    mdiAccountClock,
    mdiAccountCheck,
    mdiAccountRemove,
    mdiCheck,
    mdiClose,

    loading: true,
    userFound: false,
    messageSnackbar: false,
    avatarFailed: false,

    user: {
      name: '',
      username: '',
      bio: '',
      profile_phrase: '',
      avatar: '',
      created_at: '',
    },
    connections: [],
    connectionsCount: 0,

    // Connection status with current user
    connectionStatus: 'none', // none, pending, accepted, rejected
    connectionDirection: '', // incoming, outgoing
    connectionId: null,
    connectionLoading: false,

    endpoint: process.env.VUE_APP_ROOT_API,
    defaultAvatar: require('@/assets/images/avatars/1.png'),
  }),
  computed: {
    connectionLabel() { return t(this.connectionsCount === 1 ? '{{count}} connection' : '{{count}} connections', { count: this.connectionsCount }) },
    biographyLabel() {
      if (this.contentType === 1) return t('Bibliography')
      if (this.contentType === -1) return t('Biography / Bibliography')

      return t('Biography')
    },
    username() {
      return this.$route.params.username
    },
    avatarUrl() {
      if (this.user.avatar) {
        return this.getAvatarUrl(this.user.avatar)
      }

      return this.defaultAvatar
    },
    isLoggedIn() {
      return !!this.$store.state.jwtToken
    },
    isSelf() {
      const currentUser = this.$store.state.user
      if (!currentUser || !currentUser.extra_data) return false

      return currentUser.extra_data.username === this.username
    },
  },
  watch: {
    '$i18n.locale': async function () {
      const { locale } = this.$i18n
      try {
        const { data } = await axios.get(`${this.endpoint}/getPublicUser`, { params: { username: this.username } })
        if (locale === this.$i18n.locale && data.content && data.content[0]) this.user = { ...this.user, ...data.content[0] }
      } catch (_) { /* Preserve the current profile while offline. */ }
    },
  },
  mounted() {
    this.loadUser()
  },
  methods: {
    getAuthHeaders() {
      const token = this.$store.state.jwtToken
      if (token) {
        return { Authorization: token }
      }

      return {}
    },

    getAvatarUrl(avatar) {
      if (avatar) {
        return /^https?:\/\//i.test(avatar) ? avatar : `https://self-biography-system.s3.us-east-2.amazonaws.com/${avatar}`
      }

      return this.defaultAvatar
    },

    async loadUser() {
      this.loading = true
      try {
        const userResponse = await axios.get(
          `${this.endpoint}/getPublicUser?username=${encodeURIComponent(this.username)}`,
          { headers: this.getAuthHeaders() },
        )

        if (userResponse.data.content && userResponse.data.content.length) {
          const [profile] = userResponse.data.content
          this.user = profile
          this.avatarFailed = false
          this.userFound = true
          this.$emit('user-loaded', this.user)

          // Load connections and connection status in parallel
          this.loadPublicConnections()
          if (this.isLoggedIn && !this.isSelf) {
            this.loadConnectionStatus()
          }
        } else {
          this.userFound = false
        }
      } catch {
        this.userFound = false
      }
      this.loading = false
    },

    async loadPublicConnections() {
      try {
        const response = await axios.get(
          `${this.endpoint}/getPublicConnections?username=${encodeURIComponent(this.username)}`,
        )
        this.connections = response.data.connections || []
        this.connectionsCount = response.data.count || 0
        this.$emit('connections-loaded', {
          connections: this.connections,
          count: this.connectionsCount,
        })
      } catch {
        // Silent fail
      }
    },

    async loadConnectionStatus() {
      try {
        const response = await axios.get(
          `${this.endpoint}/getConnectionStatus?username=${encodeURIComponent(this.username)}`,
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = response.data.status || 'none'
        this.connectionDirection = response.data.direction || ''
        this.connectionId = response.data.connection_id || null
      } catch {
        this.connectionStatus = 'none'
      }
    },

    async sendRequest() {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/sendConnectionRequest`,
          { username: this.username },
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = 'pending'
        this.connectionDirection = 'outgoing'
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    async respondRequest(action) {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/handleConnectionRequest`,
          { connectionId: this.connectionId, action },
          { headers: this.getAuthHeaders() },
        )
        if (action === 'accept') {
          this.connectionStatus = 'accepted'
          this.loadPublicConnections()
        } else {
          this.connectionStatus = 'none'
        }
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    async cancelRequest() {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/removeConnection`,
          { connectionId: this.connectionId },
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = 'none'
        this.connectionId = null
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    async removeConnection() {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/removeConnection`,
          { connectionId: this.connectionId },
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = 'none'
        this.connectionId = null
        this.loadPublicConnections()
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    navigateTo(routeName) {
      if (this.$route.name !== routeName) {
        this.$router.push({
          name: routeName,
          params: { username: this.username },
          query: { ...this.$route.query },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-header { padding-top: 8px; }
.profile-parchment {
  display: grid;
  grid-template-columns: 24% minmax(0, 1fr) 8%;
  align-items: center;
  gap: 16px;
  min-height: clamp(208px, 17.6vw, 280px);
  padding: 26px 5%;
  background: url('~@/assets/images/biographies/profile-parchment.webp') center / 100% 190% no-repeat;
  color: #4d2e0e;
}
.profile-avatar-wrapper { display: flex; justify-content: center; }
.profile-avatar {
  display: block;
  width: clamp(104px, 11.2vw, 176px);
  height: clamp(104px, 11.2vw, 176px);
  object-fit: cover;
  object-position: center;
  border-radius: 50%;
  filter: sepia(.95) saturate(.65);
  border: 1px solid #78522455;
}
.profile-info { min-width: 0; text-align: center; }
.profile-name {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(28px, 3.2vw, 50px);
  font-weight: 400;
  line-height: 1.15;
  color: #4d2e0e;
  overflow-wrap: anywhere;
  margin: 0;
}
.profile-phrase {
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-size: clamp(17px, 1.6vw, 24px);
  line-height: 1.45;
  color: #573511;
  margin: 10px 0 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.profile-stars { display: flex; justify-content: center; gap: clamp(8px, 1.2vw, 20px); margin-top: 13px; }
.profile-stars .v-icon { font-size: clamp(22px, 2vw, 32px); color: #5c3a12 !important; }
.profile-meta { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; padding-top: 8px; padding-bottom: 0; }
.profile-connections { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.profile-friends-count { margin: 0; font-size: 13px; }
.profile-friends-avatars { display: flex; gap: 0; }
.friend-avatar { border: 2px solid #bda683; margin-left: -6px; }
.friend-avatar:first-child { margin-left: 0; }
.profile-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-left: auto; }
.action-btn { text-transform: none; font-weight: 600; letter-spacing: 0; }
.profile-tabs-container { padding-top: 16px; padding-bottom: 0; }
@media (max-width: 700px) {
  .profile-parchment { grid-template-columns: 70px minmax(0, 1fr); gap: 12px; min-height: 184px; padding: 16px 24px; }
  .profile-avatar { width: 70px; height: 70px; }
  .profile-name { font-size: 24px; }
  .profile-phrase { font-size: 15px; margin-top: 8px; line-height: 1.4; }
  .profile-stars { gap: 6px; margin-top: 10px; }
  .profile-stars .v-icon { font-size: 18px; }
}
@media (max-width: 380px) {
  .profile-parchment { grid-template-columns: 60px minmax(0, 1fr); gap: 10px; padding: 16px 20px; }
  .profile-avatar { width: 60px; height: 60px; }
  .profile-name { font-size: 22px; }
  .profile-phrase { font-size: 14px; }
  .profile-stars { gap: 4px; margin-top: 8px; }
  .profile-stars .v-icon { font-size: 16px; }
}
</style>
